import { useState, useCallback } from 'react';

// ============================================================
// CONTACT FORM — modal dialog
// Posts to /api/contact, a Cloudflare Pages Function that sends
// the message via Resend. Includes a hidden honeypot field for
// basic spam protection (real users never fill it in).
// ============================================================

interface ContactFormProps {
  onClose: () => void;
}

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export function ContactForm({ onClose }: ContactFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'submitting') return;

    setState('submitting');
    setErrorMsg(null);

    try {
      const resp = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, message, company }),
      });
      const body = await resp.json().catch(() => ({}));

      if (!resp.ok || !body.ok) {
        throw new Error(body.error || 'Something went wrong. Please try again.');
      }

      setState('success');
    } catch (err) {
      setState('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }, [name, email, phone, message, company, state]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>

        {state === 'success' ? (
          <div className="contact-success">
            <div className="contact-success-icon">✓</div>
            <h3>Message sent</h3>
            <p>Thanks for reaching out — we'll get back to you soon.</p>
            <button className="btn-design" onClick={onClose} style={{ marginTop: 16 }}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 className="modal-title">Get in touch</h3>
            <p className="modal-subtitle">Questions about a design or want a consultation? Send us a message.</p>

            <label className="form-label">
              Name
              <input
                type="text"
                className="address-input form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={state === 'submitting'}
              />
            </label>

            <label className="form-label">
              Email
              <input
                type="email"
                className="address-input form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={state === 'submitting'}
              />
            </label>

            <label className="form-label">
              Phone <span className="form-optional">(optional)</span>
              <input
                type="tel"
                className="address-input form-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={state === 'submitting'}
              />
            </label>

            <label className="form-label">
              Message
              <textarea
                className="address-input form-input form-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                disabled={state === 'submitting'}
                rows={4}
              />
            </label>

            {/* Honeypot — hidden from real users via CSS, bots that autofill everything get caught */}
            <label className="form-honeypot" aria-hidden="true">
              Company
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </label>

            {state === 'error' && errorMsg && (
              <div className="form-error">{errorMsg}</div>
            )}

            <button
              type="submit"
              className="btn-design"
              style={{ width: '100%', marginTop: 12 }}
              disabled={state === 'submitting'}
            >
              {state === 'submitting' ? 'Sending...' : 'Send message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
